import { useState } from "react";
import {
  ActivityIndicator,
  NativeSyntheticEvent,
  StyleSheet,
  TextInput,
  TextInputFocusEventData,
  TextInputProps,
  View,
} from "react-native";
import { P, match } from "ts-pattern";
import { useDelayedLoader } from "../hooks/useDelayedLoader";
import { animDuration, colors, invariantColor, radii } from "../utils/design";
import { Box } from "./Box";
import { Fill, Space } from "./Space";
import { Label, Text } from "./Text";

const styles = StyleSheet.create({
  input: {
    // smallFlat font style
    fontFamily: "geist",
    fontWeight: "400",
    fontSize: 13,
    lineHeight: 13,

    color: invariantColor.white,
    paddingHorizontal: 16,
    paddingVertical: 14,
    borderWidth: 1,
    borderColor: colors.neutral[700],
    borderRadius: radii[4],
    backgroundColor: colors.neutral[800],
    outlineWidth: 0,
    transitionDuration: animDuration[200],
    transitionProperty: "border-color",
  },
  inputDark: {
    backgroundColor: colors.neutral[900],
  },
  inputFocused: {
    borderColor: colors.neutral[200],
  },
  inputError: {
    borderColor: colors.negative[500],
  },
  inputDisabled: {
    backgroundColor: colors.neutral[600],
    color: colors.neutral[200],
    cursor: "not-allowed",
  },
});

export type InputTextProps = TextInputProps & {
  inputRef?: React.Ref<TextInput>;
  label?: string;
  description?: string;
  validating?: boolean;
  error?: string;
  hideError?: boolean;
  dark?: boolean;
};

export const InputText: React.FC<InputTextProps> = ({
  inputRef,
  label,
  maxLength,
  description,
  validating = false,
  error,
  hideError = false,
  dark = false,
  style,
  onFocus,
  onBlur,
  ...props
}) => {
  const [focused, setFocused] = useState(false);
  const showValidating = useDelayedLoader(validating, 500);

  const handleFocus = (e: NativeSyntheticEvent<TextInputFocusEventData>) => {
    setFocused(true);
    onFocus?.(e);
  };

  const handleBlur = (e: NativeSyntheticEvent<TextInputFocusEventData>) => {
    setFocused(false);
    onBlur?.(e);
  };

  const valueLength = props.value?.length ?? 0;

  return (
    <View style={style}>
      {label != null && (
        <>
          <Box direction="row" alignItems="center">
            <Label>
              {label}
              {props["aria-required"] === true && <Text color={colors.negative[500]}> *</Text>}
            </Label>

            {showValidating && (
              <>
                <Space width={8} />
                <ActivityIndicator size="small" color={colors.neutral[50]} />
              </>
            )}

            {maxLength != null && (
              <>
                <Fill minWidth={8} />
                <Text
                  variant="miniFlat"
                  color={valueLength > maxLength ? colors.negative[500] : colors.primary[500]}
                >
                  {valueLength}/{maxLength}
                </Text>
              </>
            )}
          </Box>

          <Space height={8} />
        </>
      )}

      <TextInput
        ref={inputRef}
        style={[
          styles.input,
          dark && styles.inputDark,
          error != null && styles.inputError,
          focused && styles.inputFocused,
          props["aria-disabled"] === true && styles.inputDisabled,
        ]}
        placeholderTextColor={colors.neutral[200]}
        onFocus={handleFocus}
        onBlur={handleBlur}
        {...props}
      />

      {match({ description, error, hideError })
        .with({ description: P.string, error: P.nullish }, ({ description }) => (
          <>
            <Space height={4} />
            <Text variant="small" color={colors.neutral[200]}>
              {description}
            </Text>
          </>
        ))
        .with({ hideError: false }, ({ error }) => (
          <>
            <Space height={4} />
            <Text variant="small" color={colors.negative[500]}>
              {error ?? " "}
            </Text>
          </>
        ))
        .otherwise(() => null)}
    </View>
  );
};
